import React, { useMemo } from 'react';
import { Box, Checkbox, FormControl, FormHelperText, FormLabel, Input, makeStyles, MenuItem, Select, Theme } from '@material-ui/core';
import _ from 'lodash';
import { fade } from '@material-ui/core/styles';
import { useField } from 'formik';
import Chip from './Chip';
import { allRegions, Country, Geography, WorldRegion } from '../types/types';
import { titleCase } from '../lib';

enum GeographyType {
  country = 'country',
  state = 'state',
  worldRegion = 'worldRegion'
}

const getGeoType = (g: Geography | null): GeographyType | null => {
  if (g === null) {
    return null;
  } else if (g.state === '' && g.country !== '' && g.worldRegion !== '') {
    return GeographyType.country;
  } else if (g.state !== '' && g.country === 'united states' && g.worldRegion === 'north america') {
    return GeographyType.state;
  } else if (g.state === '' && g.country === '' && g.worldRegion !== '') {
    return GeographyType.worldRegion;
  } else {
    return null;
  }
};

const getLabel = (geo: Geography) => {
  if (getGeoType(geo) === GeographyType.worldRegion) {
    return titleCase(geo.worldRegion);
  } else if (getGeoType(geo) === GeographyType.country) {
    return titleCase(geo.country);
  } else if (getGeoType(geo) === GeographyType.state) {
    return titleCase(geo.state);
  } else {
    return '';
  }
};

interface GeoStylesProps {
  error: boolean,
}

const useStyles = makeStyles<Theme, GeoStylesProps>((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(1),
    '& .MuiFormLabel-root': {
      transition: 'none',
      transform: 'none',
      margin: 0,
      fontSize: 14,
      fontWeight: 500
    },
    '& label + .MuiInput-formControl': {
      marginTop: 5
    },
    '& .MuiInput-underline': {
      '&:before': {
        content: 'none'
      },
      '&:after': {
        content: 'none'
      }
    },
    '& .MuiInput-input': {
      border: (props) => (props.error ? '1px solid #EA6172' : '1px solid #D0D6DA'),
      borderRadius: 4,
      fontSize: 16,
      fontWeight: 300,
      padding: '9px 12px',
      transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: 250, delay: 0 }),
      '&:focus': {
        boxShadow: `${fade(theme.palette.primary.main, 0.25)} 0 0 0 0`,
        borderColor: theme.palette.primary.main
      }
    },
    '& .MuiSelect-root': {
      '&:focus': {
        backgroundColor: 'white'
      }
    },
    '& .MuiFormHelperText-root': {
      color: theme.palette.error.main
    }
  }
}));

interface GeographySelectProps {
  id: string
  label: string
  labelId: string
  value: Geography | Geography[] | null
  touched: any
  error: any
  showAnyCase: boolean
  multiple?: boolean
  onBlur: (event: any) => void
}

const stringToGeo = (stringArray: string[]) => stringArray.map((i) => JSON.parse(i) as Geography);
const geoToString = (geo: Geography[] | Geography | null) => {
  if (Array.isArray(geo)) {
    return geo.map((i) => JSON.stringify(i));
  } else if (geo === null) {
    return JSON.stringify(null);
  } else if (geo.country === null && geo.state === null && geo.worldRegion === null) {
    return JSON.stringify(null);
  } else {
    return JSON.stringify(geo);
  }
};

const GeographySelect: React.FC<GeographySelectProps> = ({
  id,
  label,
  labelId,
  multiple = false,
  showAnyCase,
  touched,
  error,
  onBlur,
  value
}) => {
  const classes = useStyles({ error: error !== undefined });
  const [,, helpers] = useField({ name: id });
  const handleChange = (event: any, child: React.ReactNode) => {
    const selectedString = (child! as React.ReactElement).props.value as string;
    const selected = JSON.parse(selectedString) as Geography;
    if (multiple) {
      const newStringList = (event.target as HTMLInputElement).value as unknown as string[];
      const newList = stringToGeo(newStringList);
      const anySelected = !!(selected.worldRegion) && selected.worldRegion === WorldRegion.Any;
      const worldRegionSelected = !!(selected.worldRegion) && !selected.country;
      const countrySelected = !!(selected.country) && !selected.state;
      const stateSelected = !!(selected.state);
      const itemWasAdded = _.filter(newList, (i) => ((i.worldRegion === selected.worldRegion) && (i.country === selected.country) && (i.state === selected.state))).length === 1;
      if (anySelected) {
        if (itemWasAdded) {
          _.remove(newList, (i) => i.worldRegion !== WorldRegion.Any);
        } else {
          _.remove(newList, (i) => i.worldRegion === WorldRegion.Any);
        }
        helpers.setValue(newList);
      } else if (worldRegionSelected) {
        if (itemWasAdded) {
          _.remove(newList, (i) => i.country && i.worldRegion === selected.worldRegion);
        } else {
          _.remove(newList, (i) => i.state === selected.state && i.country === selected.country && i.worldRegion === selected.worldRegion);
        }
        helpers.setValue(newList);
      } else if (countrySelected) {
        if (itemWasAdded) {
          _.remove(newList, (i) => i.state !== '' && i.country === selected.country);
        } else {
          _.remove(newList, (i) => i.country === selected.country);
        }
        helpers.setValue(newList);
      } else if (stateSelected) {
        if (!itemWasAdded) {
          _.remove(newList, (i) => i.country === selected.country && i.state === selected.state);
        }
        helpers.setValue(newList);
      }
    } else {
      helpers.setValue(selected);
    }
  };

  // need to convert JSON object to string and store that in data-value of MenuItem DOM element
  const valueString = geoToString(value);

  // menu options rendering
  const menuOptionElements = useMemo(() => {
    const getMenuItemStyle = (geo: Geography | null) => {
      if (geo === null) {
        return { display: 'none' };
      }
      let padding = 0;
      if (getGeoType(geo) === GeographyType.worldRegion) {
        padding = 15;
      } else if (getGeoType(geo) === GeographyType.country) {
        padding = 30;
      } else if (getGeoType(geo) === GeographyType.state) {
        padding = 45;
      } else {
        padding = 0;
      }
      const isSelected = itemIsSelected(geo);
      if (isSelected) {
        return {
          paddingLeft: padding,
          margin: 0,
          backgroundColor: '#EDF3F5',
          fontWeight: 500
        };
      } else {
        return {
          paddingLeft: padding,
          margin: 0
        };
      }
    };

    const itemIsSelected = (geo: Geography | null) => {
      if (geo === null) {
        return false;
      }
      let isSelected = false;
      if (multiple) {
        isSelected = (value as Geography[]).filter((i) => i.state === geo.state && i.country === geo.country && i.worldRegion === geo.worldRegion).length > 0;
      } else if (value !== null) {
        isSelected = (value as Geography).state === geo.state && (value as Geography).country === geo.country && (value as Geography).worldRegion === geo.worldRegion;
      }
      return isSelected;
    };

    const optionElements: JSX.Element[] = [];


    let anyIsSelected = false;
    if (showAnyCase) {
      try {
        if ((value as Geography[]).length === 1) {
          const selected = (value as Geography[])[0];
          if (selected.worldRegion === WorldRegion.Any) {
            anyIsSelected = true;
          }
        }
      } catch (e: unknown) {
        // fail silently
      }
    }

    optionElements.push(<MenuItem style={getMenuItemStyle(null)} key='Geography None' value={JSON.stringify(null)}>Geography None</MenuItem>);
    let regions = Object.entries(allRegions);
    if (!showAnyCase) {
      regions = regions.filter(([key]) => key !== WorldRegion.Any);
    }
    regions.forEach(([, worldRegion]) => {
      const i = { worldRegion: worldRegion.value.worldRegion, country: '', state: '' };
      const regionDisabled = anyIsSelected && i.worldRegion !== WorldRegion.Any;
      optionElements.push(
        <MenuItem style={getMenuItemStyle(i)} key={getLabel(i)} value={JSON.stringify(i)} disabled={regionDisabled}>
          {multiple ? <Checkbox checked={itemIsSelected(i) || regionDisabled} /> : null }
          {getLabel(i)}
        </MenuItem>
      );
      worldRegion.children?.forEach((country) => {
        const ii = { ...i, country: country.value.country };
        const countryDisabled = (regionDisabled || itemIsSelected(worldRegion.value)) && multiple;
        optionElements.push(
          <MenuItem style={getMenuItemStyle(ii)} key={getLabel(ii)} value={JSON.stringify(ii)} disabled={countryDisabled}>
            {multiple ? <Checkbox checked={itemIsSelected(ii) || countryDisabled} /> : null }
            {getLabel(ii)}
          </MenuItem>
        );
        if (country.value.country === Country.UnitedStates) {
          country.children?.forEach((state) => {
            const iii = { ...ii, state: state.value.state };
            const stateDisabled = (regionDisabled || countryDisabled || itemIsSelected(country.value)) && multiple;
            optionElements.push(
              <MenuItem style={getMenuItemStyle(iii)} key={getLabel(iii)} value={JSON.stringify(iii)} disabled={stateDisabled}>
                {multiple ? <Checkbox checked={itemIsSelected(iii) || stateDisabled} /> : null }
                {getLabel(iii)}
              </MenuItem>
            );
          });
        }
      });
    });
    return optionElements;
  }, [multiple, value, showAnyCase]);

  return (
    <FormControl classes={classes} fullWidth>
      <FormLabel id={id}>{label}</FormLabel>
      <Select
        labelId={labelId}
        id={id}
        fullWidth
        multiple={multiple}
        native={false}
        MenuProps={{ variant: 'menu' }}
        value={valueString}
        onChange={handleChange}
        onBlur={onBlur}
        input={<Input />}
        renderValue={(v: unknown) => {
          if (multiple) {
            const val = (v as string[]).map((i) => JSON.parse(i) as Geography);
            return (<Box display='flex' flexWrap='wrap'>{val.map((i) => <Chip key={getLabel(i)} label={getLabel(i)} />)}</Box>);
          } else if (v === JSON.stringify(null)) {
            return null;
          } else {
            const val = JSON.parse(v as string) as Geography;
            return (<Box display='flex' flexWrap='wrap'><Chip key={getLabel(val)} label={getLabel(val)} /></Box>);
          }
        }}
      >
        {menuOptionElements}
      </Select>
      {touched && error ? <FormHelperText>{error}</FormHelperText> : null}
    </FormControl>
  );
};

export default GeographySelect;
